import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Search, Plus, Download, Eye, Edit, Trash, List as ListIcon, Map as MapIconLucide } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import axiosInstance from '../../../../axiosConfig';

const SOURCE_TYPES = ['Destinations', 'Hotels', 'Trains'];
const SRI_LANKA_CENTER = [7.8731, 80.7718];

// Extracts [lat, lng] from a destination record, trying every shape this API
// uses for geo data: the list endpoint's row (if it ever includes one) and
// the detail/view endpoint's nested `result.detail.geoLocation` (confirmed
// shape - see Location.jsx, which reads detail.geoLocation.latitude/longitude
// from this same view endpoint). Returns null when coordinates aren't present
// so the map can skip that pin instead of crashing or plotting [0,0].
const getCoords = (dest) => {
  const lat = dest?.detail?.geoLocation?.latitude ?? dest?.geoLocation?.latitude ?? dest?.latitude ?? dest?.lat;
  const lng = dest?.detail?.geoLocation?.longitude ?? dest?.geoLocation?.longitude ?? dest?.longitude ?? dest?.lng;
  const latNum = parseFloat(lat);
  const lngNum = parseFloat(lng);
  if (Number.isFinite(latNum) && Number.isFinite(lngNum)) {
    return [latNum, lngNum];
  }
  return null;
};

const pinIcon = (color, size = 16) =>
  L.divIcon({
    className: '',
    html: `<div style="width:${size}px;height:${size}px;border-radius:999px 999px 999px 0;background:${color};border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.4);transform:rotate(45deg);"></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
  });

// Pans the map to the selected destination without re-mounting MapContainer.
const MapFlyTo = ({ position }) => {
  const map = useMap();
  useEffect(() => {
    if (position) {
      map.flyTo(position, Math.max(map.getZoom(), 11), { duration: 0.6 });
    }
  }, [position, map]);
  return null;
};

const DestinationList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const typeFromUrl = searchParams.get('type');
  const [activeType, setActiveType] = useState(
    SOURCE_TYPES.includes(typeFromUrl) ? typeFromUrl : 'Destinations'
  );
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'map'
  const [selectedDestId, setSelectedDestId] = useState(null);
  const [resolvedCoords, setResolvedCoords] = useState({}); // { [destId]: [lat, lng] | null }
  const [resolvingCoords, setResolvingCoords] = useState(false);

  const handleTypeChange = (type) => {
    setActiveType(type);
    setSearchParams({ type }, { replace: true });
  };

  // useState's initial value only runs once on mount, so clicking a
  // different sidebar submenu link while already on this page (same route,
  // just a new ?type= param) wouldn't otherwise update the active tab - only
  // the sidebar's own highlight would move. This keeps them in sync.
  useEffect(() => {
    const urlType = searchParams.get('type');
    if (SOURCE_TYPES.includes(urlType) && urlType !== activeType) {
      setActiveType(urlType);
    }
  }, [searchParams, activeType]);

  // Tracks a destinationType filter value this component applied on the
  // user's behalf when switching to the Hotels/Trains tab, so switching back
  // to Destinations only clears an auto-applied filter, never a manual one.
  const lastAutoTypeRef = useRef(null);

  // State management
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    name: '',
    destinationType: '',
    ownershipType: '',
    status: ''
  });
  const [metaData, setMetaData] = useState({
    entityTypes: [],
    ownershipTypes: [],
    verificationStates: [],
    destinationTypes: [],
    categories: [],
    subCategories: [],
    cities: [],
    reviewTags: []
  });
  const [destinationList, setDestinationList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [error, setError] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null); // {dest, show: true}

  // Available page size options
  const pageSizeOptions = [5, 10, 20, 50];

  // Debounced filter values
  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  // Initialize pagination and filters from URL params
  useEffect(() => {
    const startFromUrl = parseInt(searchParams.get('start'), 10);
    const lengthFromUrl = parseInt(searchParams.get('length'), 10);
    const nameFromUrl = searchParams.get('name') || '';
    const destinationTypeFromUrl = searchParams.get('destinationType') || '';
    const ownershipTypeFromUrl = searchParams.get('ownershipType') || '';
    const statusFromUrl = searchParams.get('status') || '';

    // Calculate current page from start parameter
    const length = pageSizeOptions.includes(lengthFromUrl) ? lengthFromUrl : 10;
    const start = startFromUrl >= 0 ? startFromUrl : 0;
    const page = Math.floor(start / length) + 1;

    setCurrentPage(page);
    setItemsPerPage(length);
    const newFilters = {
      name: nameFromUrl,
      destinationType: destinationTypeFromUrl,
      ownershipType: ownershipTypeFromUrl,
      status: statusFromUrl
    };
    setFilters(newFilters);
    setDebouncedFilters(newFilters);
  }, [searchParams]);

  // Debounce text input filters
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters(filters);
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [filters]);

  // Update URL parameters
  const updateUrlParams = useCallback((page, size, currentFilters = debouncedFilters) => {
    const params = new URLSearchParams();
    const start = Math.max(0, (page - 1) * size);

    params.set('start', start.toString());
    params.set('length', size.toString());
    params.set('type', activeType);

    // Add filters to URL if they have values
    if (currentFilters.name) params.set('name', currentFilters.name);
    if (currentFilters.destinationType) params.set('destinationType', currentFilters.destinationType);
    if (currentFilters.ownershipType) params.set('ownershipType', currentFilters.ownershipType);
    if (currentFilters.status) params.set('status', currentFilters.status);

    setSearchParams(params, { replace: true });
  }, [setSearchParams, debouncedFilters, activeType]);

  // Handle page change
  const handlePageChange = useCallback((page) => {
    const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      updateUrlParams(page, itemsPerPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [totalItems, itemsPerPage, updateUrlParams]);

  // Handle page size change
  const handlePageSizeChange = useCallback((size) => {
    if (pageSizeOptions.includes(size)) {
      setCurrentPage(1); // Reset to first page
      setItemsPerPage(size);
      updateUrlParams(1, size);
    }
  }, [updateUrlParams]);

  // Handle filter changes
  const handleFilterChange = useCallback((filterName, value) => {
    const newFilters = { ...filters, [filterName]: value };
    setFilters(newFilters);

    // Auto-apply filters for dropdown selections (not text inputs)
    if (filterName !== 'name') {
      setCurrentPage(1);
      setDebouncedFilters(newFilters);
      updateUrlParams(1, itemsPerPage, newFilters);
    }
  }, [filters, itemsPerPage, updateUrlParams]);

  // Apply filters manually (for text inputs and manual filter button)
  const applyFilters = useCallback(() => {
    setCurrentPage(1);
    setDebouncedFilters(filters);
    updateUrlParams(1, itemsPerPage, filters);
  }, [filters, itemsPerPage, updateUrlParams]);

  // Reset filters
  const resetFilters = useCallback(() => {
    const emptyFilters = {
      name: '',
      destinationType: '',
      ownershipType: '',
      status: ''
    };
    setFilters(emptyFilters);
    setDebouncedFilters(emptyFilters);
    setCurrentPage(1);
    updateUrlParams(1, itemsPerPage, emptyFilters);
  }, [itemsPerPage, updateUrlParams]);

  const handleView = useCallback((dest) => {
    const uuid = dest.uuid || dest.destinationUuid || dest.id;
    if (!uuid) {
      alert('Destination UUID not available for this record.');
      return;
    }
    navigate(`/DestinationForm?uuid=${encodeURIComponent(uuid)}&mode=view`);
  }, [navigate]);

  const handleEdit = useCallback((dest) => {
    const uuid = dest.uuid || dest.destinationUuid;
    if (!uuid) {
      alert('Destination UUID not available for this record.');
      return;
    }
    navigate(`/DestinationForm?uuid=${encodeURIComponent(uuid)}&mode=edit`);
  }, [navigate]);

  const handleDelete = useCallback((dest) => {
    const id = dest?.id;

    if (!id) {
      console.error('Destination numeric id is missing. Full record:', dest);
      alert('Cannot process: destination id is missing.');
      return;
    }

    // Show custom confirmation dialog
    setDeleteConfirmation({ dest, show: true });
  }, []);

  const handleDeleteAction = useCallback(async (mode) => {
    if (!deleteConfirmation?.dest) return;

    const dest = deleteConfirmation.dest;
    const id = dest.id;

    // Close dialog
    setDeleteConfirmation(null);

    try {
      setFilterLoading(true);
      setError(null);

      const response = await axiosInstance.post(
        '/internal-api/v1/client-web/destination-panel/config/state-update',
        {
          id,
          state: false,
          mode: mode, // 'STATE_UPDATE' or 'REMOVE'
        }
      );

      console.log(`${mode} response:`, response.data);

      if (mode === 'STATE_UPDATE') {
        // Mark as inactive
        setDestinationList((prev) =>
          prev.map((d) => (d.id === id ? { ...d, isActive: false } : d))
        );
        alert('Destination marked as inactive successfully!');
      } else if (mode === 'REMOVE') {
        // Remove from list
        setDestinationList((prev) => prev.filter((d) => d.id !== id));
        alert('Destination deleted successfully!');
      }

    } catch (err) {
      console.error(`Error with ${mode}:`, err);
      alert(`Failed to ${mode === 'STATE_UPDATE' ? 'inactivate' : 'delete'} destination. Please try again.`);
    } finally {
      setFilterLoading(false);
    }
  }, [deleteConfirmation]);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmation(null);
  }, []);


  // Fetch metadata (only once)
  useEffect(() => {
    const fetchMetaData = async () => {
      try {
        setError(null);
        const response = await axiosInstance.get(
          '/internal-api/v1/client-web/destination-panel/registration/meta'
        );
        if (response.data?.result) {
          setMetaData(response.data.result);
        }
      } catch (error) {
        console.error("Error fetching metadata:", error);
        setError("Failed to load metadata. Please try again.");
      }
    };

    fetchMetaData();
  }, []);

  // The "Hotels" / "Trains" tabs are real destinations too - just scoped by
  // destinationType. When metadata loads (or the tab changes), look for a
  // matching real destinationType from the backend's own metadata and apply
  // it as a filter, instead of guessing an API param that may not exist.
  // "Destinations" shows everything, so it clears whatever this auto-applied
  // (without touching a destinationType the user picked manually).
  useEffect(() => {
    if (activeType === 'Destinations') {
      if (lastAutoTypeRef.current && filters.destinationType === lastAutoTypeRef.current) {
        lastAutoTypeRef.current = null;
        handleFilterChange('destinationType', '');
      }
      return;
    }

    if (!metaData.destinationTypes || metaData.destinationTypes.length === 0) return;

    const match = metaData.destinationTypes.find((t) =>
      (t.name || '').toLowerCase().includes(activeType.toLowerCase().replace(/s$/, ''))
    );

    if (match && match.code !== filters.destinationType) {
      lastAutoTypeRef.current = match.code;
      handleFilterChange('destinationType', match.code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeType, metaData.destinationTypes]);

  // Fetch destination list
  useEffect(() => {

    const fetchDestinationList = async () => {
      if (currentPage < 1 || itemsPerPage < 1) return;

      try {
        setFilterLoading(true);
        setError(null);

        const start = (currentPage - 1) * itemsPerPage;
        const params = {
          start: start,
          length: itemsPerPage,
        };

        // Add filters to params if they have values
        if (debouncedFilters.name) params.name = debouncedFilters.name;
        if (debouncedFilters.destinationType) params.destinationType = debouncedFilters.destinationType;
        if (debouncedFilters.ownershipType) params.ownershipType = debouncedFilters.ownershipType;
        if (debouncedFilters.status) params.status = debouncedFilters.status;

        const response = await axiosInstance.get(
          '/internal-api/v1/client-web/destination-panel/config/list',
          { params }
        );

        if (response.data?.data) {
          setDestinationList(response.data.data);
          setTotalItems(
            typeof response.data.recordsFiltered === 'number'
              ? response.data.recordsFiltered
              : (typeof response.data.recordsTotal === 'number'
                  ? response.data.recordsTotal
                  : (typeof response.data.total === 'number' ? response.data.total : 0))
          );
        } else {
          setDestinationList([]);
          setTotalItems(0);
        }
      } catch (error) {
        console.error("Error fetching destinations:", error);
        setError("Failed to load destinations. Please try again.");
        setDestinationList([]);
        setTotalItems(0);
      } finally {
        setFilterLoading(false);
        setLoading(false);
      }
    };

    fetchDestinationList();
  }, [currentPage, itemsPerPage, debouncedFilters, activeType]);

  // The list endpoint doesn't return coordinates, only the fields shown in the
  // table. When the map view is opened, fetch each visible destination's
  // detail/view endpoint (the same one Location.jsx reads geoLocation from)
  // to resolve real pin positions - only for rows not already resolved, and
  // only while the map is actually being viewed.
  useEffect(() => {
    if (viewMode !== 'map') return;

    const pending = destinationList.filter((dest) => {
      const id = dest.id;
      if (id === undefined || id === null) return false;
      if (Object.prototype.hasOwnProperty.call(resolvedCoords, id)) return false;
      return getCoords(dest) === null; // already has coords on the row - skip fetch
    });

    if (pending.length === 0) return;

    let cancelled = false;
    setResolvingCoords(true);

    Promise.allSettled(
      pending.map(async (dest) => {
        const uuid = dest.uuid || dest.destinationUuid;
        if (!uuid) return { id: dest.id, coords: null };
        try {
          const response = await axiosInstance.get(
            `/internal-api/v1/client-web/destination-panel/config/${uuid}/view`
          );
          const detail = response.data?.result;
          const coords = getCoords(detail);
          return { id: dest.id, coords };
        } catch (err) {
          console.error(`Error fetching location for destination ${uuid}:`, err);
          return { id: dest.id, coords: null };
        }
      })
    ).then((results) => {
      if (cancelled) return;
      setResolvedCoords((prev) => {
        const next = { ...prev };
        results.forEach((result) => {
          if (result.status === 'fulfilled') {
            next[result.value.id] = result.value.coords;
          }
        });
        return next;
      });
      setResolvingCoords(false);
    });

    return () => {
      cancelled = true;
    };
  }, [viewMode, activeType, destinationList, resolvedCoords]);

  const getStatusBadgeClass = (status) => {
    return status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600';
  };

  const getVerifiedBadgeClass = (verified) => {
    switch (verified) {
      case 'Verified':
        return 'bg-green-100 text-green-700';
      case 'Pending':
        return 'bg-amber-100 text-amber-700';
      case 'Not Verified':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-slate-100 text-slate-600';
    }
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;

  // Generate pagination pages with ellipsis
  const generatePaginationPages = () => {
    const pages = [];
    const maxVisiblePages = 7; // Show more pages for better navigation
    const sidePages = 2; // Pages to show on each side of current page

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total pages is less than or equal to maxVisiblePages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      // Calculate the range of pages to show around current page
      let startPage = Math.max(2, currentPage - sidePages);
      let endPage = Math.min(totalPages - 1, currentPage + sidePages);

      // Adjust range if we're near the beginning
      if (currentPage <= sidePages + 1) {
        endPage = Math.min(totalPages - 1, maxVisiblePages - 2);
      }

      // Adjust range if we're near the end
      if (currentPage >= totalPages - sidePages) {
        startPage = Math.max(2, totalPages - maxVisiblePages + 2);
      }

      // Add ellipsis before middle pages if needed
      if (startPage > 2) {
        pages.push('...');
      }

      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      // Add ellipsis after middle pages if needed
      if (endPage < totalPages - 1) {
        pages.push('...');
      }

      // Always show last page (if there's more than 1 page)
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const paginationPages = generatePaginationPages();

  const destinationsWithCoords = useMemo(
    () => destinationList
      .map((dest) => ({ dest, coords: getCoords(dest) ?? resolvedCoords[dest.id] ?? null }))
      .filter((item) => item.coords !== null),
    [destinationList, resolvedCoords]
  );

  const selectedCoords = useMemo(() => {
    const found = destinationsWithCoords.find((item) => item.dest.id === selectedDestId);
    return found ? found.coords : null;
  }, [destinationsWithCoords, selectedDestId]);

  const mapBounds = useMemo(() => {
    if (destinationsWithCoords.length === 0) return null;
    return destinationsWithCoords.map((item) => item.coords);
  }, [destinationsWithCoords]);

  if (error) {
    return (
      <div className="flex items-center justify-center flex-1">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center flex-1">
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 border-b-2 border-blue-600 rounded-full animate-spin"></div>
          <div className="text-slate-500">Loading destinations...</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Page header */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Sources</h1>
          <div className="mt-1 text-sm text-slate-500">
            Home <span className="mx-1.5 text-slate-400">/</span>
            Sources <span className="mx-1.5 text-slate-400">/</span>
            <span className="font-semibold text-slate-700">{activeType}</span>
          </div>
        </div>
        <div className="flex gap-2">
          <a href="/DestinationForm" className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white transition-colors rounded-lg shadow-sm bg-brand-blue hover:bg-brand-blue-dark">
            <Plus size={14} />
            Add Destination
          </a>
          <button className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold transition-colors bg-white border rounded-lg border-slate-200 text-slate-700 hover:bg-slate-50">
            <Download size={14} />
            Export
          </button>
        </div>
      </div>

      {/* Type switcher + view toggle */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="inline-flex gap-1 p-1 bg-white border rounded-lg border-slate-200">
          {SOURCE_TYPES.map((type) => (
            <button
              key={type}
              onClick={() => handleTypeChange(type)}
              className={`px-4 h-9 rounded-md text-sm font-semibold transition-colors ${
                activeType === type ? 'bg-brand-blue text-white' : 'text-slate-700 hover:bg-slate-100'
              }`}
            >
              {type}
            </button>
          ))}
        </div>
        <div className="inline-flex gap-1 p-1 bg-white border rounded-lg border-slate-200">
          <button
            onClick={() => setViewMode('list')}
            className={`flex items-center gap-1.5 px-4 h-9 rounded-md text-sm font-semibold transition-colors ${
              viewMode === 'list' ? 'bg-brand-blue text-white' : 'text-slate-700 hover:bg-slate-100'
            }`}
          >
            <ListIcon size={14} />
            List
          </button>
          <button
            onClick={() => setViewMode('map')}
            className={`flex items-center gap-1.5 px-4 h-9 rounded-md text-sm font-semibold transition-colors ${
              viewMode === 'map' ? 'bg-brand-blue text-white' : 'text-slate-700 hover:bg-slate-100'
            }`}
          >
            <MapIconLucide size={14} />
            Map
          </button>
        </div>
      </div>

      {viewMode === 'map' ? (
        <div className="overflow-hidden bg-white border rounded-xl border-slate-200">
          {/* Page Size Selector */}
          <div className="flex items-center justify-between p-4 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Show</span>
              <select
                value={itemsPerPage}
                onChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}
                className="h-8 px-2 py-1 text-xs bg-white border rounded-md border-slate-200 focus:outline-none focus:border-brand-blue"
              >
                {pageSizeOptions.map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
              <span className="text-xs text-slate-500">entries</span>
            </div>
            <div className="text-xs text-slate-500">
              Total: {totalItems} destinations
            </div>
          </div>

          <div className="grid gap-4 p-4 lg:grid-cols-[1fr_340px]" style={{ height: 620 }}>
          <div className="relative overflow-hidden bg-white border rounded-xl border-slate-200">
            <MapContainer
              center={SRI_LANKA_CENTER}
              zoom={7}
              bounds={mapBounds || undefined}
              boundsOptions={{ padding: [40, 40] }}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {selectedCoords && <MapFlyTo position={selectedCoords} />}
              {destinationsWithCoords.map(({ dest, coords }) => (
                <Marker
                  key={dest.id}
                  position={coords}
                  icon={pinIcon(dest.id === selectedDestId ? '#0055A5' : '#15803d', dest.id === selectedDestId ? 20 : 16)}
                  eventHandlers={{ click: () => setSelectedDestId(dest.id) }}
                >
                  <Popup>
                    <div style={{ minWidth: 160 }}>
                      <div style={{ fontWeight: 700, fontSize: 13 }}>{dest.name}</div>
                      <div style={{ fontSize: 11, color: '#64748b', margin: '2px 0 6px' }}>
                        {dest.destinationType} &middot; {dest.ownershipType}
                      </div>
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs ${getStatusBadgeClass(dest.isActive ? 'Active' : 'Inactive')}`}>
                        {dest.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
            <div className="absolute z-[1000] flex items-center gap-4 px-3 py-2 text-xs bg-white border rounded-lg shadow left-4 bottom-4 border-slate-200">
              <div className="flex items-center gap-1.5 text-slate-700">
                <span className="inline-block w-2.5 h-2.5 bg-green-700 rounded-full" /> Active
              </div>
              <div className="flex items-center gap-1.5 text-slate-700">
                <span className="inline-block w-2.5 h-2.5 rounded-full bg-brand-blue" /> Selected
              </div>
            </div>
          </div>

          <div className="flex flex-col p-3 overflow-y-auto bg-white border rounded-xl border-slate-200">
            <div className="flex items-center gap-2 px-2 py-2 text-xs font-semibold text-slate-500">
              {resolvingCoords && <div className="w-3 h-3 border-b-2 rounded-full animate-spin border-brand-blue" />}
              {resolvingCoords
                ? 'Locating destinations...'
                : `${destinationsWithCoords.length} of ${destinationList.length} on this page have map locations`}
            </div>
            <div className="flex flex-col gap-1">
              {!resolvingCoords && destinationsWithCoords.length === 0 ? (
                <div className="px-2 py-6 text-sm text-center text-slate-400">
                  No destinations on this page have coordinates yet.
                </div>
              ) : (
                destinationsWithCoords.map(({ dest }) => (
                  <button
                    key={dest.id}
                    onClick={() => setSelectedDestId(dest.id)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                      selectedDestId === dest.id ? 'bg-brand-blue/5 border border-brand-blue/20' : 'border border-transparent hover:bg-slate-50'
                    }`}
                  >
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${selectedDestId === dest.id ? 'bg-brand-blue' : 'bg-green-700'}`} />
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-semibold truncate text-slate-900">{dest.name}</span>
                      <span className="block text-xs truncate text-slate-500">{dest.cityName}</span>
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-xs flex-shrink-0 ${getStatusBadgeClass(dest.isActive ? 'Active' : 'Inactive')}`}>
                      {dest.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </button>
                ))
              )}
            </div>
          </div>
          </div>

          {/* Pagination Controls */}
          {totalItems > 0 && (
            <div className="flex flex-col items-center justify-between gap-3 p-4 border-t sm:flex-row border-slate-200">
              <div className="text-xs text-slate-500">
                Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} destinations
              </div>
              <div className="flex items-center space-x-1">
                {/* First Page */}
                <button
                  disabled={currentPage === 1 || filterLoading}
                  onClick={() => handlePageChange(1)}
                  className="flex items-center h-8 px-2 text-xs transition-colors border rounded-md border-slate-200 text-slate-500 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
                  title="First page"
                >
                  <span className="hidden sm:inline">First</span>
                  <span className="sm:hidden">««</span>
                </button>

                {/* Previous Page */}
                <button
                  disabled={currentPage === 1 || filterLoading}
                  onClick={() => handlePageChange(currentPage - 1)}
                  className="flex items-center h-8 px-2 text-xs transition-colors border rounded-md border-slate-200 text-slate-500 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
                  title="Previous page"
                >
                  <span className="hidden sm:inline">Previous</span>
                  <span className="sm:hidden">‹</span>
                </button>

                {/* Page Numbers */}
                <div className="flex items-center space-x-1">
                  {paginationPages.map((page, index) => (
                    page === '...' ? (
                      <span
                        key={`ellipsis-${index}`}
                        className="flex items-center justify-center w-8 h-8 text-xs cursor-default text-slate-400"
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={`page-${page}`}
                        onClick={() => handlePageChange(page)}
                        disabled={filterLoading}
                        className={`h-8 w-8 rounded-md text-xs font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                          page === currentPage
                            ? 'bg-brand-blue text-white border border-brand-blue shadow-sm'
                            : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        {page}
                      </button>
                    )
                  ))}
                </div>

                {/* Next Page */}
                <button
                  disabled={currentPage === totalPages || filterLoading}
                  onClick={() => handlePageChange(currentPage + 1)}
                  className="flex items-center h-8 px-2 text-xs transition-colors border rounded-md border-slate-200 text-slate-500 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
                  title="Next page"
                >
                  <span className="hidden sm:inline">Next</span>
                  <span className="sm:hidden">›</span>
                </button>

                {/* Last Page */}
                <button
                  disabled={currentPage === totalPages || filterLoading}
                  onClick={() => handlePageChange(totalPages)}
                  className="flex items-center h-8 px-2 text-xs transition-colors border rounded-md border-slate-200 text-slate-500 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
                  title="Last page"
                >
                  <span className="hidden sm:inline">Last</span>
                  <span className="sm:hidden">»»</span>
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <>
      {/* Filter Section */}
      <div className="p-4 mb-4 bg-white border rounded-xl border-slate-200">
        <h2 className="mb-4 text-sm font-bold text-slate-900">Filter Destinations</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <label className="block mb-1.5 text-xs font-semibold text-slate-700">Name</label>
            <div className="relative">
              <Search size={14} className="absolute text-slate-400 -translate-y-1/2 left-3 top-1/2" />
              <input
                type="text"
                value={filters.name}
                onChange={(e) => handleFilterChange('name', e.target.value)}
                className="w-full py-2.5 pl-9 pr-3 text-sm border rounded-lg border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue"
                placeholder="Enter destination name"
              />
            </div>
          </div>
          <div>
            <label className="block mb-1.5 text-xs font-semibold text-slate-700">Destination Type</label>
            <select
              value={filters.destinationType}
              onChange={(e) => handleFilterChange('destinationType', e.target.value)}
              className="w-full px-3 py-2.5 text-sm bg-white border rounded-lg border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue"
            >
              <option value="">Select destination type</option>
              {metaData.destinationTypes.map((type) => (
                <option key={type.id} value={type.code}>{type.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block mb-1.5 text-xs font-semibold text-slate-700">Ownership Type</label>
            <select
              value={filters.ownershipType}
              onChange={(e) => handleFilterChange('ownershipType', e.target.value)}
              className="w-full px-3 py-2.5 text-sm bg-white border rounded-lg border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue"
            >
              <option value="">Select ownership type</option>
              {metaData.ownershipTypes.map((type) => (
                <option key={type.id} value={type.code}>{type.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block mb-1.5 text-xs font-semibold text-slate-700">Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full px-3 py-2.5 text-sm bg-white border rounded-lg border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue"
            >
              <option value="">All Status</option>
              <option value="Active">Active</option>
              <option value="Suspended">Suspended</option>
              <option value="Deactivated">Deactivated</option>
            </select>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
          <div className="text-xs text-slate-500">
            {Object.values(filters).some(filter => filter !== '') && (
              <span>Filters applied &mdash; </span>
            )}
            {filters.name && <span className="bg-brand-blue/10 text-brand-blue px-2.5 py-1 rounded-full text-xs font-semibold mr-1">Name: {filters.name}</span>}
            {filters.destinationType && <span className="bg-brand-blue/10 text-brand-blue px-2.5 py-1 rounded-full text-xs font-semibold mr-1">Type: {filters.destinationType}</span>}
            {filters.ownershipType && <span className="bg-brand-blue/10 text-brand-blue px-2.5 py-1 rounded-full text-xs font-semibold mr-1">Ownership: {filters.ownershipType}</span>}
            {filters.status && <span className="bg-brand-blue/10 text-brand-blue px-2.5 py-1 rounded-full text-xs font-semibold mr-1">Status: {filters.status}</span>}
          </div>
          <div className="flex gap-2">
            <button
              onClick={applyFilters}
              disabled={filterLoading}
              className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white transition-colors rounded-lg bg-brand-blue hover:bg-brand-blue-dark disabled:opacity-60"
            >
              {filterLoading ? (
                <>
                  <div className="w-3 h-3 border-b-2 border-white rounded-full animate-spin"></div>
                  <span>Filtering...</span>
                </>
              ) : (
                <span>Apply Filters</span>
              )}
            </button>
            <button
              onClick={resetFilters}
              disabled={filterLoading}
              className="px-4 py-2 text-sm font-semibold transition-colors bg-white border rounded-lg border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-60"
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Destination Table */}
      <div className="overflow-hidden bg-white border rounded-xl border-slate-200">
        {/* Page Size Selector */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">Show</span>
            <select
              value={itemsPerPage}
              onChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}
              className="h-8 px-2 py-1 text-xs bg-white border rounded-md border-slate-200 focus:outline-none focus:border-brand-blue"
            >
              {pageSizeOptions.map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
            <span className="text-xs text-slate-500">entries</span>
          </div>
          <div className="text-xs text-slate-500">
            Total: {totalItems} destinations
          </div>
        </div>

        <div className="relative overflow-x-auto">
          {filterLoading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white bg-opacity-75">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-b-2 border-blue-600 rounded-full animate-spin"></div>
                <div className="text-xs text-slate-500">Filtering...</div>
              </div>
            </div>
          )}
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="px-5 py-3 text-xs font-semibold text-left border-b text-slate-500 border-slate-200 bg-slate-50">Name</th>
                <th className="px-3 py-3 text-xs font-semibold text-left border-b text-slate-500 border-slate-200 bg-slate-50">Type</th>
                <th className="px-3 py-3 text-xs font-semibold text-left border-b text-slate-500 border-slate-200 bg-slate-50">Ownership</th>
                <th className="px-3 py-3 text-xs font-semibold text-left border-b text-slate-500 border-slate-200 bg-slate-50">City</th>
                <th className="px-3 py-3 text-xs font-semibold text-left border-b text-slate-500 border-slate-200 bg-slate-50">Status</th>
                <th className="px-3 py-3 text-xs font-semibold text-left border-b text-slate-500 border-slate-200 bg-slate-50">Verified</th>
                <th className="px-3 py-3 text-xs font-semibold text-left border-b text-slate-500 border-slate-200 bg-slate-50">Actions</th>
              </tr>
            </thead>
            <tbody>
              {destinationList.length > 0 ? (
                destinationList.map(dest => (
                  <tr key={dest.id} className="transition-colors hover:bg-slate-50">
                    <td className="px-5 py-3.5 font-semibold text-slate-900 border-b border-slate-100">{dest.name}</td>
                    <td className="px-3 py-3.5 text-slate-700 border-b border-slate-100">{dest.destinationType}</td>
                    <td className="px-3 py-3.5 text-slate-700 border-b border-slate-100">{dest.ownershipType}</td>
                    <td className="px-3 py-3.5 text-slate-700 border-b border-slate-100">{dest.cityName}</td>
                    <td className="px-3 py-3.5 border-b border-slate-100">
                      <span className={`inline-flex items-center h-[22px] px-2.5 rounded-full text-xs font-semibold ${getStatusBadgeClass(dest.isActive ? 'Active' : 'Inactive')}`}>
                        {dest.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-3 py-3.5 border-b border-slate-100">
                      <span className={`inline-flex items-center h-[22px] px-2.5 rounded-full text-xs font-semibold ${getVerifiedBadgeClass(dest.verificationStatus || 'Not Verified')}`}>
                        {dest.verificationStatus || 'Not Verified'}
                      </span>
                    </td>
                    <td className="px-3 py-3.5 border-b border-slate-100">
                      <div className="flex gap-1.5">
                        <button
                          className="flex items-center justify-center w-7 h-7 transition-colors bg-white border rounded-full border-slate-200 text-slate-500 hover:bg-slate-100"
                          onClick={() => handleView(dest)}
                          title="View"
                        >
                          <Eye size={13} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleEdit(dest)}
                          className="flex items-center justify-center w-6 h-6 text-xs border rounded-full bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100"
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(dest)}
                          disabled={filterLoading}
                          className="flex items-center justify-center w-7 h-7 transition-colors bg-white border rounded-full border-slate-200 text-slate-500 hover:bg-red-50 hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Deactivate"
                        >
                          <Trash size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="px-3 py-10 text-sm text-center text-slate-500">
                    No destinations found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {totalItems > 0 && (
          <div className="flex flex-col items-center justify-between gap-3 p-4 border-t sm:flex-row border-slate-200">
            <div className="text-xs text-slate-500">
              Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} destinations
            </div>
            <div className="flex items-center space-x-1">
              {/* First Page */}
              <button
                disabled={currentPage === 1 || filterLoading}
                onClick={() => handlePageChange(1)}
                className="flex items-center h-8 px-2 text-xs transition-colors border rounded-md border-slate-200 text-slate-500 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
                title="First page"
              >
                <span className="hidden sm:inline">First</span>
                <span className="sm:hidden">««</span>
              </button>

              {/* Previous Page */}
              <button
                disabled={currentPage === 1 || filterLoading}
                onClick={() => handlePageChange(currentPage - 1)}
                className="flex items-center h-8 px-2 text-xs transition-colors border rounded-md border-slate-200 text-slate-500 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
                title="Previous page"
              >
                <span className="hidden sm:inline">Previous</span>
                <span className="sm:hidden">‹</span>
              </button>

              {/* Page Numbers */}
              <div className="flex items-center space-x-1">
                {paginationPages.map((page, index) => (
                  page === '...' ? (
                    <span
                      key={`ellipsis-${index}`}
                      className="flex items-center justify-center w-8 h-8 text-xs cursor-default text-slate-400"
                    >
                      ...
                    </span>
                  ) : (
                    <button
                      key={`page-${page}`}
                      onClick={() => handlePageChange(page)}
                      disabled={filterLoading}
                      className={`h-8 w-8 rounded-md text-xs font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                        page === currentPage
                          ? 'bg-brand-blue text-white border border-brand-blue shadow-sm'
                          : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      {page}
                    </button>
                  )
                ))}
              </div>

              {/* Next Page */}
              <button
                disabled={currentPage === totalPages || filterLoading}
                onClick={() => handlePageChange(currentPage + 1)}
                className="flex items-center h-8 px-2 text-xs transition-colors border rounded-md border-slate-200 text-slate-500 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
                title="Next page"
              >
                <span className="hidden sm:inline">Next</span>
                <span className="sm:hidden">›</span>
              </button>

              {/* Last Page */}
              <button
                disabled={currentPage === totalPages || filterLoading}
                onClick={() => handlePageChange(totalPages)}
                className="flex items-center h-8 px-2 text-xs transition-colors border rounded-md border-slate-200 text-slate-500 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
                title="Last page"
              >
                <span className="hidden sm:inline">Last</span>
                <span className="sm:hidden">»»</span>
              </button>
            </div>
          </div>
        )}
      </div>
        </>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirmation?.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-md p-6 mx-4 bg-white rounded-xl shadow-xl">
            <h3 className="mb-4 text-lg font-semibold text-slate-800">
              Destination Action
            </h3>
            <p className="mb-6 text-sm text-slate-600">
              What would you like to do with "{deleteConfirmation.dest?.name}"?
            </p>
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
              <button
                onClick={handleCancelDelete}
                className="px-4 py-2 text-sm transition-colors border rounded-lg border-slate-300 text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteAction('STATE_UPDATE')}
                className="px-4 py-2 text-sm text-white transition-colors bg-amber-500 rounded-lg hover:bg-amber-600"
              >
                Mark as Inactive
              </button>
              <button
                onClick={() => handleDeleteAction('REMOVE')}
                className="px-4 py-2 text-sm text-white transition-colors bg-red-600 rounded-lg hover:bg-red-700"
              >
                Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DestinationList;
